import type { IConnectionModel, ITileManagerModel } from '@/interfaces/models';
import type { TileInstance } from '@/models/tiles/TileInstance';
import { tileDefinitionRegistry } from '@/models/tiles/TileRegistry';
import { TileType } from '@/models/TileTypes';
import type { GraphBuildQueueItem } from '@/models/tiles/TileDefinition';
import type { NodeHandle } from './RuntimeHandles';
import { LogicGraph } from './LogicGraph';
import { captureNodeRuntimeState } from './LogicNodeInstance';
import type { Side } from '@/schemas/common';
import {
  hasNativeTopologyBuilders,
  isPatchedTopology,
  type TopologySnapshot,
} from './GraphTopology';

const gates = new Set<TileType>([TileType.Not, TileType.Diode, TileType.And]);
const boundaries = new Set<TileType>([
  ...gates,
  TileType.Wire,
  TileType.SingleInterface,
  TileType.MultiInterface,
  TileType.Lever,
  TileType.Button,
  TileType.Clock,
  TileType.Delay,
  TileType.Random,
  TileType.Structure,
]);

/** Gates own their nodes; unlike wires, changing them cannot split or merge a net.
 * Reuse the existing boundary nodes and run the native builders for just the
 * changed gates and their immediate neighbors. Wire/chip/bundle edits continue
 * through the component compiler. Validate the complete plan before mutating. */
export function patchScalarGates(
  previous: TopologySnapshot,
  next: TopologySnapshot,
  graph: LogicGraph,
  manager: ITileManagerModel,
  connections: IConnectionModel | undefined,
) {
  if (
    !isPatchedTopology(next) ||
    !hasNativeTopologyBuilders(previous) ||
    !hasNativeTopologyBuilders(next)
  )
    return;
  const changed = new Set<string>();
  const connectionChanges: string[] = [];
  // Preserve union order without allocating an array and Set for the whole file.
  for (const snapshot of [previous, next])
    for (const id of snapshot.keys()) {
      if (snapshot === next && previous.has(id)) continue;
      const before = previous.get(id);
      const after = next.get(id);
      if (before === after) continue;
      if (id.startsWith('chip:') && before?.signature !== after?.signature) return;
      if (
        before &&
        after &&
        before.owner === after.owner &&
        (before.nodeIds.size !== after.nodeIds.size ||
          [...before.nodeIds].some((node) => !after.nodeIds.has(node)))
      )
        return;
      if (
        !before ||
        !after ||
        before.owner !== after.owner ||
        before.authoredSignature !== after.authoredSignature
      ) {
        if (id.startsWith('chip:')) return;
        for (const entry of [before, after]) {
          if (
            entry &&
            (!entry.tileType ||
              !gates.has(entry.tileType) ||
              entry.ownerChip ||
              !entry.authoredSignature)
          )
            return;
        }
        changed.add(id);
      } else if (before.signature !== after.signature) connectionChanges.push(id);
    }
  if (!changed.size) return;

  // A neighboring wire may change its connected-side flags, but no existing
  // wire-to-wire link may change: that would require net splitting/merging.
  for (const id of connectionChanges) {
    const oldLinks = new Set(previous.get(id)?.connectionLinks ?? []);
    const newLinks = new Set(next.get(id)?.connectionLinks ?? []);
    for (const link of [...oldLinks, ...newLinks]) {
      if (oldLinks.has(link) === newLinks.has(link)) continue;
      const endpoint = link.slice(link.indexOf(':') + 1, link.lastIndexOf(':'));
      if (!changed.has(endpoint)) return;
    }
  }

  const removedNodes = new Set<NodeHandle>();
  const local = new Map<string, TileInstance>();
  for (const id of changed) {
    for (const node of previous.get(id)?.nodeIds ?? []) {
      if (!graph.nodes.has(node) || graph.nodes.get(node)!.type === 'WIRE') return;
      removedNodes.add(node);
    }
    const tile = next.get(id)?.owner as TileInstance | undefined;
    if (tile) local.set(id, tile);
    for (const snapshot of [previous, next]) {
      for (const neighbor of snapshot.get(id)?.neighbors ?? []) {
        if (neighbor.startsWith('chip:')) return;
        const entry = next.get(neighbor);
        if (entry) local.set(neighbor, entry.owner as TileInstance);
      }
    }
  }
  for (const tile of local.values()) {
    if (
      !boundaries.has(tile.tileType) ||
      tile.meta.bundle ||
      tile.meta.cable ||
      tile.laneNodeIds ||
      tile.adapterPin ||
      tile.adapterPort ||
      tile.cablePort
    )
      return;
    if (changed.has(tile.id)) continue;
    if (tile.tileType !== TileType.Structure && !tile.logicNodeIds) return;
    if (
      tile.tileType !== TileType.Structure &&
      tile.tileType !== TileType.MultiInterface &&
      !tile.logicNodeIds?.top
    )
      return;
    for (const node of Object.values(tile.logicNodeIds ?? {})) {
      if (
        !graph.nodes.has(node) ||
        removedNodes.has(node) ||
        graph.nodes.get(node)?.isBundleObserver
      )
        return;
    }
  }
  // An insertion has no old nodes to retire, so sharing cannot invalidate it.
  // Deletions/replacements still verify ownership across the complete snapshot.
  if (removedNodes.size) {
    for (const [id, entry] of previous) {
      if (changed.has(id)) continue;
      for (const node of entry.nodeIds) if (removedNodes.has(node)) return;
    }
  }

  const updated = new LogicGraph(graph.ids);
  for (const [id, node] of graph.nodes)
    if (!removedNodes.has(id)) updated.nodes.set(id, node);
  for (const edge of graph.edges.values()) {
    if (!removedNodes.has(edge.sourceNodeId) && !removedNodes.has(edge.targetNodeId))
      updated.addEdge(edge.sourceNodeId, edge.targetNodeId, edge.meta);
  }
  const savedStates = new Map<string, Partial<Record<Side, any>>>();
  const tiles = [...changed].flatMap((id) => {
    const tile = next.get(id)?.owner as TileInstance | undefined;
    if (!tile) return [];
    const definition = tileDefinitionRegistry.get(tile.tileType);
    const saved = definition.saveState?.({ instance: tile, graph });
    savedStates.set(
      id,
      saved ??
        Object.fromEntries(
          Object.entries(tile.logicNodeIds ?? {}).flatMap(([side, nodeId]) => {
            const node = graph.nodes.get(nodeId);
            return node ? [[side, captureNodeRuntimeState(node)]] : [];
          }),
        ),
    );
    tile.logicNodeIds = undefined;
    return [tile];
  });
  // Bound the builders' spatial access. Every external edge of a gate is one
  // cell away; retained boundary builders never traverse their unchanged nets.
  const restricted = new Proxy({} as ITileManagerModel, {
    get(_target, key) {
      const target = manager;
      if (key === 'tiles') return [...local.values()];
      if (key === 'get')
        return (point: import('@/interfaces/models').IPoint) => {
          const tile = target.get(point);
          return tile && local.has(tile.id) ? tile : undefined;
        };
      return Reflect.get(target, key, target);
    },
  });
  const build = (tile: TileInstance, source?: GraphBuildQueueItem) =>
    tileDefinitionRegistry.get(tile.tileType).buildAndConnectNodes?.({
      graph: updated,
      tileManager: restricted,
      connectionModel: connections,
      instance: tile,
      sourceNodeId: source?.sourceNodeId,
      sourceTileId: source?.sourceTileId,
      entryDirection: source?.entryDirection,
    }) ?? [];
  // Allocate gate nodes first. Then connect only edges incident to an edited
  // gate, preserving every edge and node inside the existing circuit.
  for (const tile of tiles) build(tile);
  for (const tile of local.values()) {
    for (const item of build(tile)) {
      if (changed.has(tile.id) || changed.has(item.tile.id)) build(item.tile, item);
    }
  }
  for (const tile of tiles) {
    const definition = tileDefinitionRegistry.get(tile.tileType);
    const savedState = savedStates.get(tile.id)!;
    if (definition.restoreState)
      definition.restoreState({ instance: tile, graph: updated, savedState });
    else
      for (const [side, id] of Object.entries(tile.logicNodeIds ?? {})) {
        if (savedState[side as Side] !== undefined)
          updated.nodes.get(id)?.restoreRuntimeState(savedState[side as Side]);
      }
    for (const [side, id] of Object.entries(tile.logicNodeIds ?? {})) {
      const node = updated.nodes.get(id);
      const key = JSON.stringify(['tile', tile.id, side]);
      if (node && (!node.runtimeKey || key < node.runtimeKey)) node.runtimeKey = key;
    }
  }
  return { graph: updated, tiles, tileIds: changed, previousNodeIds: removedNodes };
}
